
import { useStore } from "@/store";
import {
  defineComponent,
  computed,
  ref,
  onMounted,
  onActivated,
  inject,
} from "vue";
import swal from "sweetalert2";
import { onBeforeRouteUpdate, useRouter, useRoute } from "vue-router";
import moment from "moment";
import { api } from "@/services/Api";
import { useI18n } from "vue-i18n";
import {
  PaymentType,
  PriceTypeCategory,
  CouponDiscountType,
  CouponType,
  CouponWhoFinances,
  OrderStatus,
} from "@/models/Enums";
import ViewHeader from "@/components/ViewHeader.vue";
import BasketItems from "@/components/BasketItems.vue";
import {
  CashPayment,
  Customer,
  NotAvailableProducts,
  Order,
} from "@/models/Order";
import q from "q";
import mixin from "@/mixin";
import _ from "lodash";
import { OrderDetailsResponse } from "@/models/Interfaces";
import { Coupon } from "@/models/Product";
import { getCustomerTotalToPay } from "@/store";
import { VIRTUAL_SLOT_ID, FAKE_SLOT_ID_PREFIX } from '@/models/Constants';

export default defineComponent({
  name: "ProductsBasket",
  components: {
    ViewHeader,
    BasketItems,
  },
  async setup(props) {
    const router = useRouter();
    const route = useRoute();
    const { t } = useI18n();
    const store = useStore();
    const coupons = computed(() => store.state.coupons as Coupon[]);
    const notAvailableProducts = ref<NotAvailableProducts[]>([]);
    const isSave = computed(() => router.currentRoute.value.meta?.save);
    const isVoucher = computed(() => router.currentRoute.value.meta?.voucher);
    const orderId = computed(() => store.state.order.id);
    const products = computed(() => store.state.order.products);
    const emitter: any = inject('emitter');

    const total = computed(() => {
        return mixin.methods.formatAmount(getCustomerTotalToPay(products.value, store.state.order.businessClient?.isSubsidized ?? false))
      }     
    );

    const totalDiscount = computed(() => {
      return products.value.reduce((acc, curr) => {
        return (acc +=
          (curr.priceLine?.priceForOne ?? 0) *
          (curr.priceLine?.quantity ?? 0));
      }, 0)
        -
        products.value.reduce((acc, curr) => {
          return (acc +=
            (curr.priceLine?.priceForOne ?? 0) *
            (curr.priceLine?.quantity ?? 0) -
            (curr.priceLine?.discount ?? 0));
        }, 0);
    });

    emitter.on('order-validation-error', validation);

    async function validation() {
      if (store.state.orderValidation) {
        const validationResponse = await store.state.orderValidation;
        store.state.orderValidation = null;
        const validation = validationResponse.data;
        if (validation?.notAvailableProducts && validation?.notAvailableProducts.length > 0) {
          notAvailableProducts.value = validation.notAvailableProducts;
          swal.fire({
            icon: "warning",
            text: t("alert.exceeded-quantity"),
            customClass: {
              confirmButton: "save-button-wrapper popup-bookit-button my-1 px-4",
            },
            buttonsStyling: false,
          });
          return null;
        }
        if (validation?.voucherResult) {
          if (validation.voucherResult.resultId != 0) {
            const cancel = await cancelOrder(store.state.order.id, validation.voucherResult.errorMessage + t('alert.continue-anyway'));
            if(!cancel) router.push({ name: "selectPayment" });
          }
          else if (validation.voucherResult.checkBenefitsDiscount && validation.voucherResult.benefitsDiscount != totalDiscount.value) {
           const cancel = await cancelOrder(store.state.order.id, t('alert.different-discount', { discount: validation.voucherResult.benefitsDiscount }));
           if(!cancel) router.push({ name: "selectPayment" });
          }
        }
      }
    }

    onBeforeRouteUpdate(async () => {
      await validation();
    });

    await validation();

    function ValidateBeforeSave() {
      return !store.state.order.products.some((x) => x.notValidCoupon);
    }
    async function checkOut() {
      if (store.state.order.products.length == 0) {
        return;
      }

      const price = store.state.priceTypes.find(
        (x) => x.id == store.state.order.priceType
      );
      if (!price) {
        return;
      }
      const valid = ValidateBeforeSave();
      if (!valid) {
        return;
      }

      const order = _.cloneDeep(store.state.order);
      const budgets = _.cloneDeep(store.state.budgets);
      order.voucherCode = store.state.scanValue;
      order.products.forEach((orderProduct) => {
        orderProduct.startDateTime =
          orderProduct.date + "T" + orderProduct.slotStartTime;
      });

      store.dispatch('validateOrder', { order, budgets });

      if (price.category & PriceTypeCategory.Group) {
        router.push({ name: "groupsCustomerDetails" });
      } else {
        router.push({ name: "customerDetails" });
      }
    }

    async function SetOrderDetails(orderDetails: OrderDetailsResponse) {
      if (orderDetails.customer == null) {
        orderDetails.customer = new Customer();
      }
      const order: Order = JSON.parse(JSON.stringify(orderDetails));

      order.products.forEach((product) => {
        const couponsForCurrentProduct = coupons.value.filter(
          (x) => x.productIds.includes(product.id)
        );
        if (product.priceLine.couponId) {
          var coup = couponsForCurrentProduct.find(
            (x) => x.id == product.priceLine.couponId
          );
          if (coup) {
            product.priceLine.coupon = coup;
          }
        }
      });
      order.isReserved = true;
      order.payment = store.state.order.payment;
      order.customer.remarks = order.remarks;
      store.commit("setOrder", order);
    }

    async function SaveOrder() {
      notAvailableProducts.value = [];
      const order = _.cloneDeep(store.state.order);
      const budgets = _.cloneDeep(store.state.budgets);
      order.voucherCode = store.state.scanValue;
      order.products.forEach((orderProduct) => {
        orderProduct.startDateTime =
          orderProduct.date + "T" + orderProduct.slotStartTime;
      });

      swal.showLoading();
      const responsePromise = api.SaveOrder(order, budgets);
      await q.delay(400);
      const response = await responsePromise;
      if (response.errorMessage) {
        swal.fire({
          icon: "error",
          title: t("error-pop-up.oops"),
          text: response.errorMessage,
        });
        return null;
      }
      if (!response.data) {
        swal.close();
        return null;
      }
      if (response.data.notAvailableProducts.length > 0) {
        notAvailableProducts.value = response.data.notAvailableProducts;
        swal.fire({
          icon: "warning",
          text: t("alert.exceeded-quantity"),
          customClass: {
            confirmButton: "save-button-wrapper popup-bookit-button my-1 px-4",
          },
          buttonsStyling: false,
        });
        return null;
      }
      if (response.data.voucherResult) {
        if (response.data.voucherResult.resultId != 0) {
          const cancel = await cancelOrder(response.data.orderId, response.data.voucherResult.errorMessage + t('alert.continue-anyway'));
          if (cancel) return;
        }
        else if (response.data.voucherResult.checkBenefitsDiscount && response.data.voucherResult.benefitsDiscount != totalDiscount.value) {
          const cancel = await cancelOrder(response.data.orderId, t('alert.different-discount', { discount: response.data.voucherResult.benefitsDiscount }));
          if (cancel) return;
        }
      }
      swal.close();
      return {
        orderId: response.data.orderId,
        orderNumber: response.data.orderNumber,
        orderDetails: response.data.order
      };
    }

    async function forceReplaceSlotId(newSlotId: number | string | undefined, productId: number) {
      if (!newSlotId) {
        return;
      }
      const dateStart = route.params.dateStart;

      const newPath = `/product/${productId}/price-lines/${newSlotId}/${dateStart}`;
      await router.replace({ path: newPath, query: route.query });
    }

    async function cancelOrder(orderId, text) {
      const confirm = await swal.fire({
        icon: "error",
        title: t("error-pop-up.oops"),
        html: text,
        showDenyButton: true,
        denyButtonText: t('alert.no'),
        confirmButtonText: t('alert.yes'),
        customClass: {
          confirmButton: "popup-bookit-button",
          denyButton: "close-button-wrapper popup-bookit-button",
        },
        buttonsStyling: false,
      });
      if (confirm.isDenied) {
        await api.withLoader().SetOrderStatus(orderId, OrderStatus.CN);
      }
      return confirm.isDenied;
    }

    function pay() {
      router.push({ name: "selectPayment" });
    }

    function getDisplayDate(d: string) {
      return moment(d).format("YYYY-MM-DD");
    }

    const isToPayment = computed(() => {
      return (
        router.currentRoute.value.name == "customerDetails" ||
        router.currentRoute.value.name == "groupsCustomerDetails"
      );
    });

    const currencySign = computed(() => {
      return store.state.currencies.find(
        (x) => x.id == store.state.order.currencyId
      )?.symbol;
    });

    async function saveOrderAction() {
      if (store.state.order.products.length == 0) {
        return;
      }

      const price = store.state.priceTypes.find(
        (x) => x.id == store.state.order.priceType
      );
      if (!price) {
        return;
      }
      const valid = ValidateBeforeSave();
      if (!valid) {
        return;
      }
      const response = await SaveOrder();

      if (!response) {
        return;
      }
      const isSlotFakeId = route.params.slotId?.toString().startsWith(FAKE_SLOT_ID_PREFIX);
      const selectedProduct = store.state.selectedProduct;
      if (isSlotFakeId && selectedProduct) {
        const productId = selectedProduct.productId;
        const newSlotId = response.orderDetails.products.find(x => 
          x.id == productId 
          && x.startDateTime == `${selectedProduct.date}T${selectedProduct.slotStartTime}`
          && x.slotEndTime == selectedProduct.slotEndTime
        )?.slotId
        await forceReplaceSlotId(newSlotId, productId);
      }
      await SetOrderDetails(response.orderDetails);
      store.commit("clearVirtualSlots");
      store.state.order.id = response.orderId;
      store.state.order.orderNumber = response.orderNumber;
      if (price.category & PriceTypeCategory.Group) {
        router.push({ name: "groupsCustomerDetailsSave" });
      } else {
        router.push({ name: "customerDetailsSave" });
      }
    }

    return {
      products,
      total,
      checkOut,
      getDisplayDate,
      isToPayment,
      pay,
      currencySign,
      saveOrderAction,
      orderId,
      isSave,
      isVoucher,
      notAvailableProducts,
    };
  },
});
